/**
 * Client-side PDF thumbnail generator using PDF.js
 * Generates thumbnail from first page of PDF
 */

import * as pdfjsLib from 'pdfjs-dist';
import { getPdfjsWorkerUrl } from '@/lib/pdfjs-worker-url';

// Set up PDF.js worker (same-origin bundle — reliable on iOS Safari)
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfjsWorkerUrl();
}

export interface ThumbnailOptions {
  width?: number; // Target width in pixels (default: 300)
  height?: number; // Target height in pixels (default: 400)
  scale?: number; // Scale factor (default: 1.5 for better quality)
}

/**
 * Generate thumbnail from PDF file
 * @param file - PDF File object
 * @param options - Thumbnail generation options
 * @returns Promise resolving to base64 data URL of thumbnail image
 */
export async function generateThumbnailFromPDF(
  file: File,
  options: ThumbnailOptions = {}
): Promise<string | null> {
  try {
    const {
      width = 300,
      height = 400,
      scale = 1.5,
    } = options;

    // Read file as array buffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    // Get first page
    const page = await pdf.getPage(1);
    
    // Calculate viewport
    const viewport = page.getViewport({ scale });
    
    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) {
      console.error('[PDF Thumbnail] Failed to get canvas context');
      return null;
    }
    
    // Calculate scale to fit target dimensions
    const scaleX = width / viewport.width;
    const scaleY = height / viewport.height;
    const scaleToFit = Math.min(scaleX, scaleY);
    
    // Create scaled viewport
    const scaledViewport = page.getViewport({ scale: scale * scaleToFit });
    
    // Set canvas dimensions to match scaled viewport
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    
    // Render page to canvas (pdfjs-dist RenderParameters)
    const renderContext = {
      canvasContext: context,
      viewport: scaledViewport,
      canvas,
    };
    
    await page.render(renderContext as Parameters<typeof page.render>[0]).promise;
    
    // Convert canvas to image
    const imageDataUrl = canvas.toDataURL('image/png', 0.9);
    
    return imageDataUrl;
  } catch (error) {
    console.error('[PDF Thumbnail] Error generating thumbnail:', error);
    return null;
  }
}

/**
 * Convert base64 data URL to Blob
 * @param dataUrl - Base64 data URL
 * @returns Blob object
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * Convert base64 data URL to File
 * @param dataUrl - Base64 data URL
 * @param filename - Filename for the File object
 * @returns File object
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const blob = dataUrlToBlob(dataUrl);
  return new File([blob], filename, { type: 'image/png' });
}

